


import { Component, Vue } from "vue-property-decorator";

import { Filter, FilterValue, Named, Identifiable } from "@Internal/Contracts";
import { CustomError, CustomValue, SelectionFilter, TextFilter } from "@Internal/Models";

import { AdsPublications, Subscriptions } from "App";
import { EventBus, $t } from "App/IOC";
import { Column, PagerSorter } from "App/Models";

import RecordsComponent from "Web/Components/Records.vue";

/* Module-level customer cache. MultiFilter caches the filter objects and DeepCopies them, so a
   filter's autocomplete request callback can outlive the view instance it was created on (and,
   under HMR, close over a stale instance whose list is empty). Holding the loaded list at module
   scope, like the coupon filter usecase, means the Name / Portails loaders read from one stable
   source whatever instance's closure runs, so the dropdowns are never empty while the list has
   rows. Mirrors the getByCoupon pattern in UseCases/Customers/Filters. */
let customerCache: any[] = [];
let customerCacheLoaded = false;
let customerCachePromise: Promise<void> | undefined;

/* Lazy enrichment cache: memberCount / owner / portalCount / portalNames per groupId, filled row
   by row after the table paints (see enrichAll). Module scope so it survives HMR + view remounts
   and is shared with the Portails filter loader, the same way customerCache is. */
const enrichmentCache: Record<number, any> = {};
/* Guards the single background sweep so filter / pagination re-renders don't relaunch it. */
let enrichmentSweepStarted = false;

/* Groups holding an ACTIVE subscription that PAYS ( a line with a positive subtotal ). The list
   shows only them ( Fred, 02/10/2026 ): trials, offers, free and ended accounts stay out. */
async function loadPayingGroups(): Promise<Set<number>> {
    const status = new SelectionFilter( "Status", "", [], "" );

    status.Value = [ new CustomValue<string>( "Active", "Active" ) ];

    const [ subscriptions, , error ] =
        await Subscriptions.Find( [ status ], new PagerSorter( 999 ) );

    if( error !== undefined )
        throw error;

    const groups = new Set<number>();

    for( const subscription of subscriptions as any[] ) {
        const customer = subscription.Customer || {};
        const group = customer.Group !== undefined && customer.Group !== null
            ? Number( customer.Group.Id !== undefined ? customer.Group.Id : customer.Group )
            : 0;
        const pays = ( subscription.Lines || [] ).some( ( line: any ): boolean =>
            line.Subtotal !== undefined && line.Subtotal !== null
            && Number( line.Subtotal.toString() ) > 0 );

        if( group > 0 && pays && customer.IsStaff !== true )
            groups.add( group );
    }

    return groups;
}

async function loadCustomerCache(): Promise<void> {
    const [ [ customers, , error ], payingGroups ] = await Promise.all( [
        AdsPublications.GetAllCustomers( [], new PagerSorter( 999 ) ),
        loadPayingGroups().catch( ( failure: CustomError ) => failure )
    ] );

    if( error !== undefined || !( payingGroups instanceof Set ) ) {
        /* Clear so a later call can retry instead of being stuck on a failed load. */
        customerCachePromise = undefined;
        throw error !== undefined ? error : payingGroups;
    }

    customerCache = ( customers as any[] ).filter(
        ( row: any ): boolean => payingGroups.has( Number( row.Group ) ) );
    customerCacheLoaded = true;
}

/* Single shared, in-flight-guarded load: update() + both autocomplete loaders fire together and
   the filter panel re-invokes its loader on every render, so without this the slow customer-list
   request would be fired many times in parallel before the cache fills. */
function ensureCustomerCache(): Promise<void> {
    if( customerCacheLoaded )
        return Promise.resolve();

    if( customerCachePromise === undefined )
        customerCachePromise = loadCustomerCache();

    return customerCachePromise;
}

/* Sale point settings: a traditional, filterable list of the Console customers (groups). Clicking a
   row opens the group's associated portals. The /gateway/customer-list endpoint only paginates +
   text-searches server-side (it ignores company/status filters and returns no email), so the whole
   list is loaded once and filtered + paginated client-side. Defaults to active groups. */
@Component( {
    components: {
        Records: RecordsComponent
    }
} )
export default class AdsPublicationSalePointSettingsView extends Vue {
    private columns: Column[] = [];
    private customers: any[] = [];
    private pagerSorter: PagerSorter = new PagerSorter( 10 );

    private created(): void {
        this.columns = [
            Column.Static( "Owner", $t( "SALE_POINT_colOwner" ) ),
            Column.Static( "Email", $t( "SALE_POINT_colEmail" ) ),
            Column.Static( "Address", $t( "SALE_POINT_colAddress" ) ),
            Column.Static( "Group", $t( "SALE_POINT_colGroup" ) ),
            Column.Static( "Status", $t( "SALE_POINT_colStatus" ) ),
            Column.Static( "MemberCount", $t( "SALE_POINT_colMembers" ) ),
            Column.Static( "PortalCount", $t( "SALE_POINT_colPortalCount" ) ),
            Column.Static( "PortalNames", $t( "SALE_POINT_colPortals" ) )
        ];
    }

    private getFilters(): Promise<Filter[]> {
        const status = SelectionFilter.Multi(
            "Status",
            $t( "SALE_POINT_filterStatus" ),
            [
                { Id: "active", Name: $t( "SALE_POINT_statusActive" ) },
                { Id: "inactive", Name: $t( "SALE_POINT_statusInactive" ) }
            ],
            $t( "SALE_POINT_filterStatus" )
        );

        /* Default to active groups. */
        status.Value = [ { Id: "active", Name: $t( "SALE_POINT_statusActive" ) } ];

        return Promise.resolve( [
            status,
            SelectionFilter.MultiRequest(
                "Name",
                $t( "GLOBAL_filterCustomerName" ),
                this.getContacts,
                $t( "GLOBAL_filterCustomerPlaceholder" )
            ),
            new TextFilter(
                "Email",
                $t( "CUSTOMERS_filterEmail" ),
                $t( "CUSTOMERS_filterEmailPlaceholder" )
            ),
            SelectionFilter.MultiRequest(
                "Portal",
                $t( "SALE_POINT_filterPortals" ),
                this.getPortalNames,
                $t( "SALE_POINT_filterPortalsPlaceholder" )
            )
        ] );
    }

    /* Portails filter: multi-select of the distinct gateway names configured across the loaded
       groups (SeLoger, Leboncoin…), narrowed client-side. */
    private getPortalNames = async (
        keyword?: string
    ): Promise<[ FilterValue[], CustomError | undefined ]> => {
        try {
            await ensureCustomerCache();
        } catch( error ) {
            return [ [], error as CustomError ];
        }

        const seen: Record<string, boolean> = {};
        const values: FilterValue[] = [];

        for( const customer of customerCache ) {
            for( const name of this.portalNamesOf( customer ) ) {
                const clean = String( name || "" ).trim();

                if( clean === "" || seen[ clean ] )
                    continue;

                seen[ clean ] = true;
                values.push( { Id: clean, Name: clean } );
            }
        }

        values.sort( ( a, b ) => ( a as Named ).Name.localeCompare( ( b as Named ).Name ) );

        if( keyword === undefined || keyword === "" )
            return [ values, undefined ];

        const needle = keyword.toLowerCase();

        return [
            values.filter( value => ( value as Named ).Name.toLowerCase().includes( needle ) ),
            undefined
        ];
    };

    private portalNamesOf( row: any ): string[] {
        return Array.isArray( row.PortalNames ) ? row.PortalNames : [];
    }

    /* Contact (Nom du client) filter: multi-select autocomplete, narrowed client-side from the
       loaded customers (the endpoint does not partial-match on contact name). */
    private getContacts = async (
        keyword?: string
    ): Promise<[ FilterValue[], CustomError | undefined ]> => {
        try {
            await ensureCustomerCache();
        } catch( error ) {
            return [ [], error as CustomError ];
        }

        const seen: Record<string, boolean> = {};
        const values: FilterValue[] = [];

        for( const customer of customerCache ) {
            const name = customer.Owner || "";

            if( name === "" || seen[ name ] )
                continue;

            seen[ name ] = true;
            values.push( { Id: name, Name: name } );
        }

        values.sort( ( a, b ) => ( a as Named ).Name.localeCompare( ( b as Named ).Name ) );

        if( keyword === undefined || keyword === "" )
            return [ values, undefined ];

        const needle = keyword.toLowerCase();

        return [
            values.filter( value => ( value as Named ).Name.toLowerCase().includes( needle ) ),
            undefined
        ];
    };

    private isActive( status: string ): boolean {
        return String( status || "" ).toLowerCase() === "active";
    }

    private async update( filters: Filter[], pagerSorter: PagerSorter ): Promise<void> {
        try {
            await ensureCustomerCache();
        } catch( error ) {
            EventBus.Publish( "Error", error );
            return;
        }

        const rows = this.applyFilters( customerCache, filters );

        const page = pagerSorter.Page;
        const limit = pagerSorter.Limit;
        const start = ( page - 1 ) * limit;

        this.customers = rows.slice( start, start + limit );

        pagerSorter.Total = rows.length;
        this.pagerSorter = pagerSorter;

        /* Fire-and-forget: the table is already assigned, so it paints now; member + portal counts
           stream in afterwards. */
        this.enrichAll();
    }

    /* Lazy enrichment. The list loads without the heavy member / portal columns; here we sweep
       every group once, a few at a time, calling get(groupId) and patching the rows as each
       resolves. The cache is module-level, so pagination and filtering never refetch, and the
       Portails filter's loader sees the names once the sweep has run. */
    private enrichAll(): void {
        if( enrichmentSweepStarted ) {
            this.applyCachedToVisible();
            return;
        }

        enrichmentSweepStarted = true;

        const groupIds: number[] = [];
        const seen: Record<number, boolean> = {};

        for( const row of customerCache ) {
            const groupId = Number( row.Group );

            if( !groupId || seen[ groupId ] )
                continue;

            seen[ groupId ] = true;
            groupIds.push( groupId );
        }

        const CONCURRENCY = 6;
        let cursor = 0;

        const worker = async (): Promise<void> => {
            while( cursor < groupIds.length ) {
                const groupId = groupIds[ cursor++ ];
                const [ data, error ] = await AdsPublications.GetCustomerEnrichment( groupId );

                if( error !== undefined || data === undefined )
                    continue;

                enrichmentCache[ groupId ] = data;
                this.applyEnrichmentToGroup( groupId, data );
            }
        };

        for( let i = 0; i < CONCURRENCY; i++ )
            worker();
    }

    private applyCachedToVisible(): void {
        for( const row of this.customers ) {
            const data = enrichmentCache[ Number( row.Group ) ];

            if( data !== undefined )
                this.applyEnrichment( row, data );
        }
    }

    private applyEnrichmentToGroup( groupId: number, data: any ): void {
        for( const row of customerCache )
            if( Number( row.Group ) === groupId )
                this.applyEnrichment( row, data );
    }

    private applyEnrichment( row: any, data: any ): void {
        this.$set( row, "MemberCount", data.memberCount !== undefined ? data.memberCount : 0 );
        this.$set( row, "GroupOwner", data.owner || "" );
        this.$set( row, "PortalCount", data.portalCount !== undefined ? data.portalCount : 0 );
        this.$set( row, "PortalNames", Array.isArray( data.portalNames ) ? data.portalNames : [] );
    }

    /* A row's member / portal cells show a "…" placeholder until its enrichment has landed. */
    private isEnriched( row: any ): boolean {
        return enrichmentCache[ Number( row.Group ) ] !== undefined;
    }

    private applyFilters( source: any[], filters: Filter[] ): any[] {
        let rows = source;

        for( const filter of filters ) {
            if( filter.Value.length === 0 )
                continue;

            if( filter.Id === "Status" )
                rows = this.applyStatusFilter( rows, filter );

            else if( filter.Id === "Name" )
                rows = this.applyNameFilter( rows, filter );

            else if( filter.Id === "Email" )
                rows = this.applyEmailFilter( rows, filter );

            else if( filter.Id === "Portal" )
                rows = this.applyPortalFilter( rows, filter );
        }

        return rows;
    }

    private applyStatusFilter( rows: any[], filter: Filter ): any[] {
        const ids = ( filter.Value as Identifiable[] ).map( value => String( value.Id ) );
        const wantActive = ids.includes( "active" );
        const wantInactive = ids.includes( "inactive" );

        if( wantActive && wantInactive )
            return rows;

        return rows.filter( row =>
            wantActive ? this.isActive( row.Status ) : !this.isActive( row.Status )
        );
    }

    private applyPortalFilter( rows: any[], filter: Filter ): any[] {
        const selected = ( filter.Value as Identifiable[] ).map( value => String( value.Id ) );

        return rows.filter( row =>
            this.portalNamesOf( row ).some( name => selected.includes( String( name ) ) )
        );
    }

    private applyNameFilter( rows: any[], filter: Filter ): any[] {
        const names = ( filter.Value as Identifiable[] ).map( value => String( value.Id ) );

        return rows.filter( row => names.includes( String( row.Owner || "" ) ) );
    }

    private applyEmailFilter( rows: any[], filter: Filter ): any[] {
        const needle = String( filter.Value[ 0 ] || "" ).toLowerCase().trim();

        if( needle === "" )
            return rows;

        return rows.filter( row =>
            String( row.Email || "" ).toLowerCase().includes( needle )
        );
    }

    /* Single-line address from the parts the customer-list exposes (often empty upstream). */
    private addressOf( row: any ): string {
        return [ row.Street, row.PostalCode, row.City ]
            .map( ( part: any ) => String( part || "" ).trim() )
            .filter( ( part: string ) => part !== "" )
            .join( ", " );
    }

    /* Row click ("open") routes to the group's associated portals. */
    private executeAction( action: string, rows: any[] ): void {
        if( action !== "open" || rows.length === 0 )
            return;

        const groupId = Number( rows[ 0 ].Group );

        if( !groupId )
            return;

        this.$router.push( {
            name: "ads-publication-sale-point-portals",
            params: { id: String( groupId ) },
            query: {
                name: rows[ 0 ].Company || rows[ 0 ].Owner || rows[ 0 ].Name || "",
                customerId: String( rows[ 0 ].Id || "" ),
                company: rows[ 0 ].Company || "",
                owner: rows[ 0 ].Owner || "",
                email: rows[ 0 ].Email || "",
                address: this.addressOf( rows[ 0 ] )
            }
        } );
    }
}
